import { ChartElementDescriptor, FlagType, MetricType, ResidentSortKey } from "./types";
import { Resident } from './types';
import { environment } from '../environments/environment';
import { p } from "@angular/core/src/render3";
// import * as $ from "jquery";

export class Util {

  // The isInDemoMode method has been added so that variations which are intended to be implemented for demonstration
  // purposes only can easily, and conditionally, incorporated into the standard code base. To use, ensure that
  // demoMode property is added to a configuration environment and set, and then conditionally include demo code in
  // a conditional block which is true when this method is called. An independent demo environment (environment.demo.ts)
  // has been created for this purpose and can be used to serve or build in demo mode (e.g., "ng serve --port xx
  // --configuration demo")
  // 
  static isInDemoMode(): boolean {
    // @ts-ignore -- ignore error for possible expected absence of demoMode property: 
    return environment.hasOwnProperty("demoMode") && environment.demoMode;
  }

  // Given the ID of a chart, or its containing div element, this method will colour chart legend items with the
  // colour of the associated chart element.
  // args: chartID - 
  //         CSS ID value identifying the chart to use
  //       chartElementClass - 
  //         one or more space separated CSS classes which uniquely identify the set of chart elements from which
  //         colours will be copied into legend items (e.g., 'ct-slice-pie', 'ct-bar')
  //       legendSymbolClass - 
  //         one or more space separated CSS classes which uniquely identify the set of legend symbols which will be
  //         coloured (e.g., 'fa fa-circle')
  //       sourceColourPropertyName -
  //         name of the CSS property from which the source colour will be derived (e.g., 'fill', 'stroke')
  //       assumeDoubleLegendSymbolCount -
  //         This method will typically be called from appropriate angular lifecycle hooks. A side-effect is that when
  //         the method is called, and the HTML is rendered, there are twice the number of legend symbols available for
  //         colouring. If argument supplied for this parameter is set to true (default), colouring will only be
  //         applied to half the rendered symbols. If for some reason, colouring isn't being properly applied for a
  //         particular invocation of this method, simply change the argument to false. Note that if the argument is
  //         set to false anyway, colouring will be properly applied, but an inefficiency is introduced by redundant
  //         colouring.
  // 
  static colourLegend(chartID: string, chartElementClass: string, legendSymbolClass: string, 
    sourceColourPropertyName: string, assumeDoubleLegendSymbolCount: boolean = true ) {
    let chart = document.getElementById(chartID);
    if (chart != null) {
      let chartElements = chart.getElementsByClassName(chartElementClass);
      if (chartElements != null && chartElements.length > 0) {
        let chartLegendSymbols = 
          chart.getElementsByClassName(legendSymbolClass) as HTMLCollectionOf<HTMLElement>;
        if (chartLegendSymbols != null) {
          // The rendered HTML typically contains 2 sets of symbols at the time colouring is applied; only one should be
          // coloured:
          let chartLegendSymbolsCount = assumeDoubleLegendSymbolCount ? chartLegendSymbols.length / 2 : 
            chartLegendSymbols.length;
          for (let i = 0; i < chartElements.length && i < chartLegendSymbolsCount; i++) {
            chartLegendSymbols[i].style.color = 
              window.getComputedStyle(chartElements[i], null).getPropertyValue(sourceColourPropertyName);
          }
        }
      }
    }
  }

  // Given the ID of a chart, or its containing div element, this method will colour chart legend items with the
  // colour of the associated chart element.
  // args: chartID - 
  //         CSS ID value identifying the chart to use
  //       chartElementClass - 
  //         one or more space separated CSS classes which uniquely identify the set of chart elements from which
  //         colours will be copied into legend items (e.g., 'ct-slice-pie', 'ct-bar')
  //       legendSymbolClass - 
  //         one or more space separated CSS classes which uniquely identify the set of legend symbols which will be
  //         coloured (e.g., 'fa fa-circle')
  //       sourceColourPropertyName -
  //         name of the CSS property from which the source colour will be derived (e.g., 'fill', 'stroke')
  //       seriesCount -
  //         the number of series displayed in the chart. Note, this equates to the number of discrete colours used
  //         for chart elements associated with legend symbols (e.g., bar colours in a bar chart).
  //       assumeDoubleLegendSymbolCount -
  //         This method will typically be called from appropriate angular lifecycle hooks. A side-effect is that when
  //         the method is called, and the HTML is rendered, there are twice the number of legend symbols available for
  //         colouring. If argument supplied for this parameter is set to true (default), colouring will only be
  //         applied to half the rendered symbols. If for some reason, colouring isn't being properly applied for a
  //         particular invocation of this method, simply change the argument to false. Note that if the argument is
  //         set to false anyway, colouring will be properly applied, but an inefficiency is introduced by redundant
  //         colouring.
  // note:   This method differs with respect to the colourLegend method since it must retrieve colours from chart
  //         elements (for each series) in a non-sequential manner.
  static colourLegendForMultipleSeries(chartID: string, chartElementClass: string, legendSymbolClass: string, 
    sourceColourPropertyName: string, seriesCount: number, assumeDoubleLegendSymbolCount: boolean = true) {
    if (seriesCount > 0 ) {
      let chart = document.getElementById(chartID);
      if (chart != null) {
        let chartElements = chart.getElementsByClassName(chartElementClass);
        if (chartElements != null && chartElements.length > 0) {
          let chartLegendSymbols = 
            chart.getElementsByClassName(legendSymbolClass) as HTMLCollectionOf<HTMLElement>;
          if (chartLegendSymbols != null) {
            // The rendered HTML typically contains 2 sets of symbols at the time colouring is applied; only one should be
            // coloured:
            let chartLegendSymbolsCount = assumeDoubleLegendSymbolCount ? chartLegendSymbols.length / 2 : 
              chartLegendSymbols.length;
            let elementCountPerSeries = chartElements.length / seriesCount;
            for (let i = 0; i < seriesCount && i < chartElements.length && i < chartLegendSymbolsCount; i++) {
              chartLegendSymbols[i].style.color = 
                window.getComputedStyle(chartElements[(i * elementCountPerSeries)],
                  null).getPropertyValue(sourceColourPropertyName);
            }
          }
        }
      }
    }
  }

  // This method calculates the number of days between the two supplied dates.
  // args: thisDay -
  //         the first of the pair of dates.
  //       thatDay -
  //         the second of the pair of dates.
  // return: the number of integral days when thatDay is subtraced from thisDay.
  static daysSubtract(thisDay: Date, thatDay: Date): number
  {
    const MS_PER_DAY: number = 86400000;
    // Since only dates without time are needed, set input dates to midnight:
    thisDay.setHours(0, 0, 0, 0);
    thatDay.setHours(0, 0, 0, 0);

    let thisDayMS = thisDay.getTime();
    let thatDayMS = thatDay.getTime();
    let diffMS = thisDayMS - thatDayMS;

    return Math.round(diffMS / MS_PER_DAY);
  }

  // Converts a text date to a Date.
  // args: textDate - 
  //   a date as text in the format "dd/mm/yyyy" with any valid delimiter (not just "/").
  // return: a Date object corresponding to the date supplied. If the supplied date is invalid, a null will be returned.
  // TODO: validate the input date and handle appropriately
  static textDateToDate(textDate: String): Date {
    let date: Date = null;
    let isValidDate = true;
    if (textDate.length != 10 || textDate.substr(6).length != 4 || textDate.substr(3, 2).length != 2 ||
      textDate.substr(0, 2).length != 2)
      isValidDate = false;
    if (isValidDate){
      let year = +(textDate.substr(6));
      let month = +(textDate.substr(3, 2)) - 1; // since the date constructor's month parameter is 0 based
      let day = +(textDate.substr(0, 2));
      if (isNaN(day) || isNaN(month) || isNaN(year) || year < 0 || day < 1 || month < 0 || month > 11)
        isValidDate = false;
      else if (day > this.getDaysInMonth(month, year))
        isValidDate = false;
      else
      {
        date = new Date(year, month, day);
        if (isNaN(date.getTime()))
          isValidDate = false; 
      }
    }
    return isValidDate ? date : null;
  }

  // Given an array of reals, this method returns an array of its indices by ascending order of the reals' mantissas
  // (fractional parts).
  // args: realValue - 
  //         an array of real numbers from which mantissas will be extracted for sorting.
  // return: an ordered array of indices into 'realValue' where the indices are ordered by the mantissas within the
  //   'realValue' array. To illustrate, assume 'realValue' contain the following:
  //     [0] => 3.5
  //     [1] => 2.1
  //     [2] => 6.3
  //   Sequential order of the mantissas is 1, 3, and 5 and so the returned indices array will contain the associated
  //   indices in the corresponding order:
  //     indices[0] => 1
  //     indices[1] => 2
  //     indices[2] => 0
  static sortByMantissa(realValue: number[]): number[] {
    let mantissa = new Array<number>(realValue.length);
    for (let i = 0; i < mantissa.length; i++) {
      mantissa[i] = realValue[i] - Math.floor(realValue[i]);
    }
    let indices: number[] = new Array(mantissa.length).fill(0);
    for (let i = indices.length - 1; i >= 0; i--) {
      let currentMaxIndex = 0;
      for (let j = 0; j < mantissa.length; j++) {
        if (mantissa[j] > mantissa[currentMaxIndex]) {
            indices[i] = j;
          currentMaxIndex = j;
        }
      }
      mantissa[currentMaxIndex] = -mantissa[currentMaxIndex];
    }
    return indices;
  }

  // Given a set of non-negative proportions, this method returns the corresponding integral percentages. For example,
  // if the proportions, 20 and 30 are supplied, this method will return 40 and 60, since 20 is 40%, and 30 is 60% of
  // their total (50). Since rounding can lead to integral percentages that don't sum to 100, the largest remainder
  // algorithm is used to adjust individual percentages so that their total is 100, as expected.
  // args: proportion -
  //         an array containing the proportions to be converted to percentages.
  // return: an array of integral percentages in the same proportion as the input array and totalling to 100.
  // note: in the case where all proportions are equal, but don't total 100, the first value will be incremented to
  // compensate. This would occur if three equal portions (each 33 1/3) were provided, for example.
  static calculatePercentages(proportion: number[]): number[] {
    let percentage: number[] = new Array(proportion.length);
    let integralPercentage: number[] = new Array(proportion.length);
    let proportionSum: number = 0;
    let isBadInput: boolean = false;
    for (let currentProportion of proportion) {
      if (currentProportion < 0)
        isBadInput = true;
      proportionSum += currentProportion;      
    }
    if (!(proportionSum > 0)) // '== 0' doesn't work
      isBadInput = true;
    if (!isBadInput) {
      for (let i = 0; i < proportion.length; i++) {
        percentage[i] = proportion[i] / proportionSum * 100;
        integralPercentage[i] = Math.floor(percentage[i]);
      }
      let integralPercentageSum: number = 0;
      for (let currentIntegralPercentage of integralPercentage) {
        integralPercentageSum += currentIntegralPercentage;      
      }
      if (integralPercentageSum != 100) {
        let adjustedValuesCount = 100 - integralPercentageSum;
        let indicesSortedByMantissa = this.sortByMantissa(percentage);
        for (let i = indicesSortedByMantissa.length - 1;
          i >= indicesSortedByMantissa.length - adjustedValuesCount && i < integralPercentage.length; i--) {
            integralPercentage[indicesSortedByMantissa[i]]++;
        }
      }
    }
    else {
      // if division by zero occurs return something, rather than crash; in this case though, the chart will be strange.
      for (let i = 0; i < integralPercentage.length; i++)
        integralPercentage[i] = 0;
    }
    return integralPercentage;
  }

  // Given a set of proportions, this method will return an equivalent set of percentages (as integral values). For
  // example, if the proportions, 20 and 30 are supplied, this method will return 40 and 60, since 20 is 40%, and 30
  // is 60% of of their total (50).
  // args: descriptor -
  //         an array of non-negative values.
  // return: if a descriptor containing a set of non-negative values is supplied, a descriptor with a set of
  // corresponding relative percentages will be returned. If negative values are supplied or their sum is 0, this
  // method will return null.
  static calculateDescriptorPercentages(descriptor: ChartElementDescriptor[]): ChartElementDescriptor[] {
    let chartElementDescriptor: ChartElementDescriptor[] = new Array<ChartElementDescriptor>(descriptor.length);
    let proportion: number[] = new Array(descriptor.length);
    for (let i = 0; i < descriptor.length; i++) {
      proportion[i] = descriptor[i].proportion;
    }
    let percentage: number[] = Util.calculatePercentages(proportion);
    if (percentage == null) {
      chartElementDescriptor = null;
    }
    else {
      for (let i = 0; i < percentage.length; i++) {
        chartElementDescriptor[i] = { legendLabel: descriptor[i].legendLabel, proportion: percentage[i] };
      }
    }
    return chartElementDescriptor;
  }

  // This method extracts the elements from a ChartElementDescriptor[] and returns a corresponding array of labels
  // with the same ordering.
  // args: chartElementDescriptor -
  //         an array of descriptors containing a value (including "") in each of its element's 'legendLabel'
  //         properties.
  // return: an array of labels extracted from 'chartElementDescriptor' and in the same order as the descriptor's
  //   elements
  static toLegendLabelArray(chartElementDescriptor: ChartElementDescriptor[]): string[] {
    let legendLabel: string[] = new Array(chartElementDescriptor.length);
    for (let i = 0; i < chartElementDescriptor.length; i++) {
      legendLabel[i] = chartElementDescriptor[i].legendLabel;
    }
    return legendLabel;
  }

  // Extracts the elements from a ChartElementDescriptor[] and returns a corresponding array of proportions with the
  // same ordering.
  // args: chartElementDescriptor -
  //         an array of descriptors containing a value (including "") in each of its element's 'proportion'
  //         properties.
  // return: an array of proportionss extracted from 'chartElementDescriptor' and in the same order as the descriptor's
  //   elements
  static toProportionArray(chartElementDescriptor: ChartElementDescriptor[]): number[] {
    let proportion: number[] = new Array(chartElementDescriptor.length);
    for (let i = 0; i < chartElementDescriptor.length; i++) {
      proportion[i] = chartElementDescriptor[i].proportion;
    }
    return proportion;
  }

  // This method converts the provided string into its MetricType equivalent. 'undefined' is returned if a
  // corresponding MetricType doesn't exist.
  // args: word -
  //         the string to be converted.
  // return: if there is a corresponding MetricType, that will be returned, otherwise, 'undefined' will be returned.
  static toMetricType(word: string): MetricType {
    let capitalisedWord = "";
    if (word && word.length > 0)
      capitalisedWord =  word.charAt(0).toUpperCase() + word.toLowerCase().slice(1);
    return MetricType[capitalisedWord];
  }

  // This method converts the provided string into its FlagType equivalent. 'undefined' is returned if a
  // corresponding FlagType doesn't exist.
  // args: word -
  //         the string to be converted.
  // return: if there is a corresponding FlagType, that will be returned, otherwise, 'undefined' will be returned.
  static toFlagType(word: string): FlagType {
    let capitalisedWord = "";
    if (word && word.length > 0)
      capitalisedWord =  word.charAt(0).toUpperCase() + word.toLowerCase().slice(1);
    return FlagType[capitalisedWord];
  }

  // This method converts the provided string into its ResidentSortKey equivalent. ResidentSortKey.Name is used by
  // default and returned if a corresponding ResidentSortKey doesn't exist.
  // args: word -
  //         the string to be converted.
  // return: if there is a corresponding ResidentSortKey, that will be returned, otherwise, ResidentSortKey.Name will
  //   be returned.
  static toResidentSortKey(word: string): ResidentSortKey {
    let capitalisedWord = "Name";
    if (word && word.length > 0)
      capitalisedWord =  word.charAt(0).toUpperCase() + word.toLowerCase().slice(1);
    return ResidentSortKey[capitalisedWord];
  }

  // Converts a string representation of a truth value into a boolean.
  // args: booleanText -
  //         the string representation of a truth value.
  // return: if, "y", "yes", "t", or "true" is provided (in any case), 'true' will be returned, otherwise, 'false' will
  //   be returned.
  static toBoolean(booleanText: string): boolean {
    let isTrue: boolean = false;
    let isTrueText = booleanText.toLowerCase();
    if (booleanText == "y" || booleanText == "yes" || booleanText == "t" || booleanText == "true")
      isTrue = true;
    return isTrue;
  }

  // This method compares two residents according to their age and then name.
  // args: thisResident -
  //         one of the pair of residents being compared.
  //         the second of the pair of residents being compared.
  // return: '1', if 'thisResident' is older than 'thatResident', '-1', if 'thisResident' is younger than
  //   'thatResident', or, if both residents are the same age, returns 1 if 'thisResident' has a full name which appears
  //   alphabetically later than the full name of 'thatResident', '-1', if the full name of 'thisResident' appears
  //   alphabetically earlier than the full name of 'thatResident', or '0' if both names are identical.
  static residentCompareByAge(thisResident: Resident, thatResident: Resident): number {
    let matchValue: number = 0;
    if (thisResident.residentAge > thatResident.residentAge)
      matchValue = 1;
    else if (thisResident.residentAge < thatResident.residentAge)
      matchValue = -1;
    if (matchValue == 0) {
      if (thisResident.residentName > thatResident.residentName)
        matchValue = 1;
      else if (thisResident.residentName < thatResident.residentName)
        matchValue = -1;
    }
    return matchValue;
  }

  // This method compares two residents according to their gender and then name.
  // args: thisResident -
  //         one of the pair of residents being compared.
  //         the second of the pair of residents being compared.
  // return: '1', if 'thisResident' has a gender which appears alphabetically later than the gender of 'thatResident',
  //   '-1', if the gender of 'thisResident' appears alphabetically earlier than the gender of 'thatResident', or, if
  //   both residents are the same gender, returns 1 if 'thisResident' has a full name which appears alphabetically later
  //   than the full name of 'thatResident', '-1', if the full name of 'thisResident' appears alphabetically earlier
  //   than the full name of 'thatResident', or '0' if both names are identical.
  static residentCompareByGender(thisResident: Resident, thatResident: Resident): number {
    let matchValue: number = 0;
    if (thisResident.residentGender > thatResident.residentGender)
      matchValue = 1;
    else if (thisResident.residentGender < thatResident.residentGender)
      matchValue = -1;
    if (matchValue == 0) {
      if (thisResident.residentName > thatResident.residentName)
        matchValue = 1;
      else if (thisResident.residentName < thatResident.residentName)
        matchValue = -1;
    }
    return matchValue;
  }

  // This method compares two residents according to their full names.
  // args: thisResident -
  //         one of the pair of residents being compared.
  //         the second of the pair of residents being compared.
  // return: '1', if 'thisResident' has a full name which appears alphabetically later than the full name of
  //   'thatResident', '-1', if the full name of 'thisResident' appears alphabetically earlier than the full name of
  //   'thatResident', or '0' if both names are identical.
  static residentCompareByName(thisResident: Resident, thatResident: Resident): number {
    let matchValue: number = 0;
    if (thisResident.residentName > thatResident.residentName)
      matchValue = 1;
    else if (thisResident.residentName < thatResident.residentName)
      matchValue = -1;
    return matchValue;
  }

  // This method compares two residents according to their palliative care phases and then name.
  // args: thisResident -
  //         one of the pair of residents being compared.
  //         the second of the pair of residents being compared.
  // return: '1', if 'thisResident' has a phase name which appears alphabetically later than the phase name of
  //   'thatResident', '-1', if the phase name of 'thisResident' appears alphabetically earlier than the phase name of
  //   'thatResident', or, if both residents are in the same phase, returns 1 if 'thisResident' has a full name which
  //   appears alphabetically later than the full name of 'thatResident', '-1', if the full name of 'thisResident'
  //   appears alphabetically earlier than the full name of 'thatResident', or '0' if both names are identical.
  static residentCompareByPhase(thisResident: Resident, thatResident: Resident): number {
    let matchValue: number = 0;
    if (thisResident.pcPhase > thatResident.pcPhase)
      matchValue = 1;
    else if (thisResident.pcPhase < thatResident.pcPhase)
      matchValue = -1;
    if (matchValue == 0) {
      if (thisResident.residentName > thatResident.residentName)
        matchValue = 1;
      else if (thisResident.residentName < thatResident.residentName)
        matchValue = -1;
    }
    return matchValue;
  }

  // This method sorts and returns the provided set of residents by age, gender, name, or palliative care phase
  // depending upon the value of the supplied sort key.
  // args: resident -
  //         a set of residents to sort.
  //       sortkey -
  //         the key by which to sort the residents.
  // return: a copy of the original set of residents in sorted order.
  // note: an array which is a shallow copy of 'resident' is created in the body of this method which will have a
  // performance impact if a huge array is to be sorted.
  static sortResidentList(resident: Resident[], sortKey: ResidentSortKey): Resident[] {
    let sortedResident: Resident[] = resident.slice(0);
    switch (sortKey) {
      case ResidentSortKey.Age:
        sortedResident.sort(Util.residentCompareByAge);
        break;
      case ResidentSortKey.Gender:
        sortedResident.sort(Util.residentCompareByGender);
        break;
      case ResidentSortKey.Name:
        sortedResident.sort(Util.residentCompareByName);
        break;
      case ResidentSortKey.Phase:
        sortedResident.sort(Util.residentCompareByPhase);
        break;
    }
    return sortedResident;
  }

  // This method determines whether the give year is a leap year.
  // args: year -
  //         the year to test.
  // return: if the given year is a leap year, 'true' will be returned, otherwise 'false' will be returned.
  static isLeapYear(year: number): boolean {
    let isLeapYear: boolean = false;
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
      isLeapYear = true;
    return isLeapYear;
  }

  // This method returns the number of days in the given month for the provided year and takes leap years into account. 
  // args: month -
  //         the month number where January is 0 and subsequent months have a value the previous month plus 1 (up to
  //         11 for December) 
  //       year -
  //         the year for which the number of days in the given month should be calculated
  // return: if the number of days in the given month for the provided year
  static getDaysInMonth(month: number /* 0 indexed */, year: number): number {
    let dayCount: number = 31;
    switch(month) {
      case 1:
        dayCount = Util.isLeapYear(year) ? 29 : 28;
        break;
      case 3:
      case 5:
      case 8:
      case 10:
        dayCount = 30;
        break;
      default:
        dayCount = 31;
        break;
    }
    return dayCount;
  }

}